/*  Agent Memory Dashboard  (/app/agent-memory)
 *
 *  Central "training log" for the AI agents. Every time a human rejects a
 *  draft outreach message, signal, or candidate — a memory entry is saved
 *  here with the reason. These reasons are injected as "vermijd:" context
 *  into every LLM draft, making the agents progressively less likely to
 *  repeat past mistakes.
 *
 *  This page lets users:
 *     - Review what the agents have learned (with scope + category filters)
 *     - Delete incorrect memories (mistake corrections)
 *     - Add manual "always avoid" rules that aren't tied to a rejection
 *     - Understand the training signal at a glance
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import {
    Brain, Sparkles, Trash2, Plus, Loader2, X, Building2, Users, Radar, Briefcase,
} from "lucide-react";

const ENTITY_ICON = {
    workspace: Sparkles,
    lead: Building2,
    signal: Radar,
    candidate: Users,
};
const ENTITY_IDS = ["workspace", "lead", "signal", "candidate"];
const CATEGORY_IDS = ["tone", "too_long", "too_short", "wrong_hook", "generic", "pushy", "off_topic", "other"];

export default function AgentMemoryDashboard() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scope, setScope] = useState("all");     // all | workspace | lead | signal | candidate
    const [showAdd, setShowAdd] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (scope !== "all") params.entity_type = scope;
            const r = await api.get("/memory", { params });
            setRows(r.data || []);
        } catch (e) {
            toast.error(t("memory.load_failed"));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

    const stats = useMemo(() => {
        const byScope = { workspace: 0, lead: 0, signal: 0, candidate: 0 };
        const byCategory = {};
        for (const r of rows) {
            byScope[r.entity_type] = (byScope[r.entity_type] || 0) + 1;
            if (r.category) byCategory[r.category] = (byCategory[r.category] || 0) + 1;
        }
        return { byScope, byCategory, total: rows.length };
    }, [rows]);

    const remove = async (id) => {
        if (!confirm(t("memory.delete_confirm"))) return;
        setDeletingId(id);
        try {
            await api.delete(`/memory/${id}`);
            toast.success(t("memory.deleted"));
            setRows((prev) => prev.filter((r) => r.id !== id));
        } catch (e) { toast.error(t("memory.delete_failed")); }
        finally { setDeletingId(null); }
    };

    return (
        <PageHeader
            title={t("memory.title")}
            subtitle={t("memory.subtitle")}
            icon={Brain}
            actions={
                <button onClick={() => setShowAdd(true)} data-testid="memory-add-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                    <Plus size={14} /> {t("memory.add_rule")}
                </button>
            }
        >
            {/* Stats strip — 4 counters + total */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="memory-stats">
                <StatCard label={t("memory.total")} value={stats.total} icon={Brain} active testid="stat-total" />
                {ENTITY_IDS.map((k) => (
                    <StatCard key={k} label={t(`memory.entity.${k}`)} value={stats.byScope[k] || 0}
                        icon={ENTITY_ICON[k]} testid={`stat-${k}`} />
                ))}
            </div>

            {/* Scope filter pills */}
            <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="scope-filter">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("memory.scope")}</span>
                {[
                    { id: "all", label: t("memory.scope_all") },
                    { id: "workspace", label: t("memory.scope_workspace") },
                    { id: "lead", label: t("memory.scope_lead") },
                    { id: "signal", label: t("memory.scope_signal") },
                    { id: "candidate", label: t("memory.scope_candidate") },
                ].map((s) => (
                    <button key={s.id} onClick={() => setScope(s.id)} data-testid={`scope-${s.id}`}
                        className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${scope === s.id
                            ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)]"
                            : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {/* Memory list */}
            {loading ? (
                <div className="text-center text-sm text-[var(--jr-text-3)] py-12">
                    <Loader2 size={16} className="animate-spin inline mr-2" /> {t("memory.loading")}
                </div>
            ) : rows.length === 0 ? (
                <EmptyState onAdd={() => setShowAdd(true)} />
            ) : (
                <div className="space-y-2" data-testid="memory-list">
                    {rows.map((m) => (
                        <MemoryRow key={m.id} memory={m} onDelete={remove} deleting={deletingId === m.id} />
                    ))}
                </div>
            )}

            {showAdd && <AddMemoryModal onClose={() => setShowAdd(false)} onCreated={(mem) => {
                setRows((prev) => [mem, ...prev]);
                setShowAdd(false);
            }} />}
        </PageHeader>
    );
}


function StatCard({ label, value, icon: Icon, active, testid }) {
    return (
        <div data-testid={testid} className={`rounded-lg border p-3 ${active
            ? "border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/[0.06]"
            : "border-white/10 bg-[var(--jr-surface)]"}`}>
            <div className="flex items-center gap-2">
                <Icon size={13} className={active ? "text-[var(--jr-brand)]" : "text-[var(--jr-text-3)]"} />
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] truncate">{label}</div>
            </div>
            <div className={`text-2xl font-display mt-1 ${active ? "text-[var(--jr-brand)]" : "text-white"}`}>{value}</div>
        </div>
    );
}


function MemoryRow({ memory, onDelete, deleting }) {
    const { t, i18n } = useTranslation();
    const Icon = ENTITY_ICON[memory.entity_type] || Sparkles;
    const scopeLabel = ENTITY_IDS.includes(memory.entity_type) ? t(`memory.entity.${memory.entity_type}`) : memory.entity_type;
    const at = memory.created_at ? new Date(memory.created_at).toLocaleString(i18n.language === "en" ? "en-GB" : i18n.language === "fr" ? "fr-BE" : "nl-BE", { dateStyle: "short", timeStyle: "short" }) : "";
    return (
        <div data-testid={`memory-row-${memory.id}`}
            className="rounded-lg border border-white/10 bg-[var(--jr-surface)] p-4 hover:bg-[var(--jr-surface-2)] transition-colors">
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--jr-brand)]/10 grid place-items-center shrink-0">
                    <Icon size={14} className="text-[var(--jr-brand)]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.03] text-[var(--jr-text-2)]">
                            {scopeLabel}
                        </span>
                        {memory.category && (
                            <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-info)]/10 text-[var(--jr-info)]">
                                {CATEGORY_IDS.includes(memory.category) ? t(`memory.cat.${memory.category}`) : memory.category}
                            </span>
                        )}
                        <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{at}</span>
                        {memory.created_by_name && (
                            <span className="text-[10px] text-[var(--jr-text-3)]">· {t("memory.by")} {memory.created_by_name}</span>
                        )}
                    </div>
                    <p className="text-sm text-white leading-relaxed break-words">{memory.reason}</p>
                    {memory.original_snippet && (
                        <details className="mt-2">
                            <summary className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] cursor-pointer hover:text-[var(--jr-text-2)]">
                                {t("memory.show_original")}
                            </summary>
                            <p className="mt-1.5 text-xs text-[var(--jr-text-3)] italic break-words whitespace-pre-wrap rounded border border-white/8 bg-black/20 p-2">
                                {memory.original_snippet}
                            </p>
                        </details>
                    )}
                </div>
                <button onClick={() => onDelete(memory.id)} disabled={deleting}
                    data-testid={`memory-delete-${memory.id}`}
                    title={t("memory.delete_title")}
                    className="p-1.5 rounded-md text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10 disabled:opacity-50">
                    {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
            </div>
        </div>
    );
}


function EmptyState({ onAdd }) {
    const { t } = useTranslation();
    return (
        <div className="rounded-xl border border-dashed border-white/12 p-10 text-center" data-testid="memory-empty">
            <Brain size={36} className="mx-auto text-[var(--jr-text-3)] mb-3" />
            <h3 className="text-lg font-display text-white mb-1">{t("memory.empty_title")}</h3>
            <p className="text-sm text-[var(--jr-text-2)] max-w-md mx-auto mb-4">
                {t("memory.empty_body")}
            </p>
            <button onClick={onAdd} data-testid="memory-empty-add"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                <Plus size={14} /> {t("memory.add_manual_rule")}
            </button>
        </div>
    );
}


function AddMemoryModal({ onClose, onCreated }) {
    const { t } = useTranslation();
    const [reason, setReason] = useState("");
    const [category, setCategory] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!reason.trim()) return;
        setSaving(true);
        try {
            const r = await api.post("/memory", {
                reason: reason.trim(),
                category: category || null,
                entity_type: "workspace",
            });
            toast.success(t("memory.rule_added"));
            onCreated?.(r.data);
        } catch (e) { toast.error(t("memory.add_failed")); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="add-memory-modal">
            <div className="w-full max-w-xl rounded-2xl border border-white/12 bg-[var(--jr-surface)] shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <header className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--jr-brand)]/15 grid place-items-center shrink-0">
                        <Brain size={16} className="text-[var(--jr-brand)]" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">{t("memory.add_manual_rule")}</h3>
                        <p className="text-xs text-[var(--jr-text-3)] mt-0.5">{t("memory.add_modal_sub")}</p>
                    </div>
                    <button onClick={onClose} data-testid="add-memory-close"
                        className="p-1.5 rounded-md text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                        <X size={16} />
                    </button>
                </header>
                <div className="px-6 py-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-2">
                            {t("memory.rule")} <span className="text-[var(--jr-error)]">*</span>
                        </label>
                        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                            rows={5} autoFocus
                            placeholder={t("memory.rule_placeholder")}
                            data-testid="add-memory-reason"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--jr-brand)] resize-none leading-relaxed" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-2">
                            {t("memory.category_optional")}
                        </label>
                        <div className="flex flex-wrap gap-1.5" data-testid="add-memory-categories">
                            {CATEGORY_IDS.map((id) => {
                                const active = category === id;
                                return (
                                    <button key={id} onClick={() => setCategory(active ? "" : id)}
                                        data-testid={`add-cat-${id}`}
                                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active
                                            ? "bg-[var(--jr-brand)] text-black border-[var(--jr-brand)]"
                                            : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                                        {t(`memory.cat.${id}`)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2 bg-black/20">
                    <button onClick={onClose} data-testid="add-memory-cancel"
                        className="text-sm px-4 py-2 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5">
                        {t("common.cancel")}
                    </button>
                    <button onClick={submit} disabled={!reason.trim() || saving} data-testid="add-memory-confirm"
                        className="inline-flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {t("common.add")}
                    </button>
                </footer>
            </div>
        </div>
    );
}
